import { Button } from '@/components/shadcn/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { IconCheckMarkOutline, IconCloseOutline, IconDownChevronOutline } from '@/components/icons'
import { Badge } from '@/components/shadcn/badge'
import { useVirtualizer } from '@tanstack/react-virtual'
import { BaseFieldProps } from '../types'

interface Option {
  label: string
  value: string
}

interface MultiLevelComboboxProps {
  options: Option[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  variant: BaseFieldProps['variant']
}

export function MultiLevelCombobox({
  options = [],
  value = [],
  onChange,
  placeholder = 'Select options',
  disabled = false,
  variant = 'column',
}: MultiLevelComboboxProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const hasScrolledRef = useRef(false)

  // Ensure options and value are always arrays
  const safeOptions = useMemo(() => (Array.isArray(options) ? options : []), [options])
  const safeValue = useMemo(() => (Array.isArray(value) ? value : []), [value])

  // Get selected option labels
  const selectedLabels = useMemo(() => {
    return safeValue
      .map((val) => {
        const option = safeOptions.find((opt) => opt.value === val)
        return option ? option.label : null
      })
      .filter(Boolean) as string[]
  }, [safeOptions, safeValue])

  // Filter options based on search query
  const filteredOptions = useMemo(
    () =>
      searchQuery
        ? safeOptions.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
        : safeOptions,
    [safeOptions, searchQuery]
  )

  // Set up virtualization
  const rowVirtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 36, // Approximate height of each option item
    overscan: 10,
  })

  // Reset search and scroll state when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('')
      hasScrolledRef.current = false
    }
  }, [open])

  const handleSelect = useCallback(
    (optionValue: string) => {
      const isSelected = safeValue.includes(optionValue)
      let newValue: string[]

      if (isSelected) {
        // Remove the value if already selected
        newValue = safeValue.filter((val) => val !== optionValue)
      } else {
        // Add the value if not selected
        newValue = [...safeValue, optionValue]
      }

      onChange(newValue)
    },
    [onChange, safeValue]
  )

  const handleClear = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onChange([])
    },
    [onChange]
  )

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    hasScrolledRef.current = false // Reset scroll state when search changes
  }, [])

  // Render a simple list of options for debugging
  const renderSimpleOptionsList = () => {
    return (
      <div className="max-h-[300px] overflow-auto p-2">
        {filteredOptions.length === 0 ? (
          <div className="py-6 text-center text-sm">No results found.</div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = safeValue.includes(option.value)

            return (
              <div
                key={option.value}
                className={cn(
                  'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-grey-lightest',
                  isSelected && 'bg-grey-lightest'
                )}
                onClick={() => handleSelect(option.value)}
              >
                <IconCheckMarkOutline className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                {option.label}
              </div>
            )
          })
        )}
      </div>
    )
  }

  return (
    <div className="relative w-full">
      <div className="flex w-full items-center">
        <div className="flex-1">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  'w-full justify-between',
                  safeValue.length === 0 && 'text-muted-foreground',
                  variant === 'row' && 'h-6'
                )}
                disabled={disabled}
              >
                <span className="truncate">
                  {safeValue.length > 0 ? (
                    selectedLabels.length > 2 ? (
                      <div className="inline-flex items-center gap-2">
                        {selectedLabels.slice(0, 2).map((label) => (
                          <Badge key={label} variant="outline" className="bg-grey-lightest">
                            <div className="inline-block w-20 truncate">{label}</div>
                          </Badge>
                        ))}
                        <Badge variant="outline">+ {selectedLabels.length - 2}</Badge>
                      </div>
                    ) : (
                      selectedLabels.map((label) => (
                        <Badge variant="outline" key={label}>
                          <div className="inline-block w-24 truncate">{label}</div>
                        </Badge>
                      ))
                    )
                  ) : (
                    placeholder
                  )}
                </span>
                <IconDownChevronOutline className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <div className="flex flex-col">
                <div className="flex items-center border-b px-3">
                  <input
                    ref={inputRef}
                    className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoFocus
                  />
                </div>

                {/* Use simple list rendering instead of virtualization for now */}
                {renderSimpleOptionsList()}

                {/* Commented out virtualization for debugging
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm">No results found.</div>
                ) : (
                  <div 
                    ref={listRef} 
                    className="max-h-[300px] overflow-auto"
                  >
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const option = filteredOptions[virtualRow.index]
                        if (!option) {
                          return null;
                        }

                        const isSelected = safeValue.includes(option.value)

                        return (
                          <div
                            key={option.value}
                            data-index={virtualRow.index}
                            className={cn(
                              'absolute left-0 top-0 w-full',
                              'flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-grey-lightest',
                              isSelected && 'bg-grey-lightest'
                            )}
                            style={{
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            onClick={() => handleSelect(option.value)}
                          >
                            <IconCheckMarkOutline
                              className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
                            />
                            {option.label}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                */}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Clear button rendered next to the popover trigger */}
        {safeValue.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className={cn('ml-1 h-9 w-9 p-0', variant === 'row' && 'h-6 w-6')}
            onClick={handleClear}
            aria-label="Clear selection"
            disabled={disabled}
          >
            <IconCloseOutline size={12} className="shrink-0 opacity-50 hover:opacity-100" />
          </Button>
        )}
      </div>
    </div>
  )
}
